import { ResponsiveTreeMap } from '@nivo/treemap'
import { useStore } from '@/store'

// Add interface for node data
interface TreeMapNode {
  id: string;
  value?: number;
  sentiment?: number;
  summary?: string | null;
  headlines?: string[];
  children?: TreeMapNode[];
}

// Function to generate consistent random scores based on ID
function getRandomScores(id: number): number {
  // Use the ID as a seed to get consistent results
  const seed = Math.sin(id) * 10000;
  const random = Math.abs((seed - Math.floor(seed)) * 100);
  
  // Ensure the value is between 0 and 100
  return Math.min(100, Math.max(0, random));
}

export default function StreamVisualization() {
  const { headlineGroups, headlines } = useStore()

  // Transform data for nivo TreeMap
  const treeMapData: TreeMapNode = {
    id: 'root',
    children: headlineGroups?.map(hg => {
      const headlineCount = headlines?.filter(h => h.headline_group_id === hg.headline_group_id).length || 0
      const sentiment = getRandomScores(hg.headline_group_id)
      
      return {
        id: `group-${hg.headline_group_id}`,
        value: headlineCount,
        sentiment,
        summary: hg.summary,
        headlines: headlines?.filter(h => h.headline_group_id === hg.headline_group_id)
          .map(h => h.title)
      }
    }) || []
  }

  return (
    <div className="flex-1 overflow-hidden">
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-3">Stream Visualization</h2>
        <div className="w-full h-[calc(100vh-220px)]">
          <ResponsiveTreeMap<TreeMapNode>
            data={treeMapData}
            identity="id"
            value="value"
            valueFormat=".0f"
            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
            labelSkipSize={12}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
            parentLabelPosition="left"
            borderColor={{ from: 'color', modifiers: [['darker', 0.1]] }}
            nodeOpacity={1}
            colors={node => {
              const sentiment = node.data.sentiment
              // Normalize sentiment to RGB values
              if (!sentiment || sentiment <= 20) return 'rgb(255,0,0)' // Red for low sentiment
              if (sentiment >= 80) return 'rgb(0,255,0)' // Green for high sentiment
              // Calculate gradient between red and green
              const normalized = (sentiment - 20) / 60
              const red = Math.round(255 * (1 - normalized))
              const green = Math.round(255 * normalized)
              return `rgb(${red},${green},0)`
            }}
            tooltip={({ node }) => (
              <div className="bg-white p-4 shadow-lg rounded-lg border">
                <p className="font-bold mb-2">{node.data.summary}</p>
                <p className="text-sm mb-2">Articles: {node.value}</p>
                <p className="text-sm mb-2">Sentiment Score: {node.data.sentiment}</p>
                <div className="max-h-40 overflow-y-auto">
                  {node.data.headlines?.map((headline: string, i: number) => (
                    <p key={i} className="text-xs mb-1">{headline}</p>
                  ))}
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  )
} 